import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe, registerLocaleData, TitleCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
// ANGULAR LANGUAGE IMPORTS
import localeAU from '@angular/common/locales/en-AU';
import localeCA from '@angular/common/locales/en-CA';
import localeGB from '@angular/common/locales/en-GB';
import localeNZ from '@angular/common/locales/en-NZ';
/* Angular system imports */
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, provideAppInitializer, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BxColorPickerModule } from '@buildxact/ui-components/bx-color-picker';
import { BxDisclaimModule } from '@buildxact/ui-components/bx-disclaim';
import { BxDropdownMultiselectCheckboxModule } from '@buildxact/ui-components/bx-dropdowns';
import { BxSelectableThumbnailModule } from '@buildxact/ui-components/bx-selectable-thumbnail';
import { BxSpinnerModule } from '@buildxact/ui-components/bx-spinner';
import { BxStickyActionBarModule } from '@buildxact/ui-components/bx-sticky-action-bar';
import { BxTileModule } from '@buildxact/ui-components/bx-tile';
import { NgSelectModule } from '@ng-select/ng-select';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
/* Third-party modules */
import { ColorPickerModule } from 'ngx-color-picker';
import { NgxSelectModule } from 'ngx-select-ex';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app.component';
import { appConfig } from './app.config';
import { initializeApp, initializeRoute, initializeThirdParty } from './app.initializer';
import { BxGlobalNotificationsModule } from './core/components/bx-global-notifications/bx-global-notifications.module';
import { BxNoticesModule } from './core/components/bx-notices/bx-notices.module';
import { BxPermissionNotificationModule } from './core/components/bx-permission-notification/bbx-permission-notification.module';
import { CategoryBreakdownModule } from './core/components/category-breakdown/category-breakdown.component';
import { ItemBreakdownModule } from './core/components/item-breakdown/item-breakdown.component';
import { RfqResponseAttachmentsComponent } from './core/components/rfq-response-attachments/rfq-response-attachments.component';
import { RfqResponseCostingitemsComponent } from './core/components/rfq-response-costingitems/rfq-response-costingitems.component';
import { RfqResponseDetailsComponent } from './core/components/rfq-response-details/rfq-response-details.component';
import { RfqResponseMessagesComponent } from './core/components/rfq-response-messages/rfq-response-messages.component';
import { RfqResponseSideFormComponent } from './core/components/rfq-response-side-form/rfq-response-side-form.component';
import { TaxRateSelectorModule } from './core/components/tax-rate-selector/tax-rate-selector.component';
import { TaxRatesEditorModule } from './core/components/tax-rates-editor/tax-rates-editor.component';
import { CurrencyInputModule } from './core/directives/currency-input.directive';
import { FormValidationDirective } from './core/directives/form-validation.directive';
import { SanitizeInputTextDirective } from './core/directives/sanitize-input-text.directive';
import { PhoneNumberLocalizePipeModule, SelectPipeModule, SortByPipe } from './core/pipes';
import { AppVersionService } from './core/services/app-version.service';
import { AuthTokenService } from './core/services/auth-token.service';
// Ng generated angular-7 components. Need to use separate angular ng project
// as this project doesn't yet have angular project definition, but I started this
// in another branch and got it working.
import { BusyOverlayInterceptor } from './core/services/busy-overlay.interceptor';
import { BusyOverlayService } from './core/services/busy-overlay.service';
import { BxApiErrorInterceptorService } from './core/services/bx-api-error-interceptor.service';
import { BxApiErrorService } from './core/services/bx-api-error.service';
import { BxPictureInPictureService } from './core/services/bx-picture-in-picture.service';
import { CataloguesService } from './core/services/catalogues.service';
import { ColourCodeConverterService } from './core/services/colour-code-converter-service';
import { ColumnWidthsService } from './core/services/column-widths.service';
import { ConnectivityService } from './core/services/connectivity.service';
import { CustomTemplateService } from './core/services/custom-template.service';
import { DialogService } from './core/services/dialog.service';
import { EstimateCostingsService } from './core/services/estimate.costing.service';
import { EstimatesV2Service } from './core/services/estimateV2.service';
import { FullStoryService } from './core/services/fullstory.service';
import { HttpCompressionInterceptor } from './core/services/http-compression.interceptor';
import { HttpInterceptorService } from './core/services/http-interceptor.service';
import { InvoiceSharingService } from './core/services/invoice-sharing.service';
import { LeadsService } from './core/services/leads.service';
import { LocalStorageService } from './core/services/local-storage.service';
import { MessagingService } from './core/services/messaging.service';
import { NotificationService } from './core/services/notification.service';
import { PasswordService } from './core/services/password.service';
import { PendoFeedbackService } from './core/services/pendo-feedback.service';
import { StoresService } from './core/services/stores';
import { TaxProfileService } from './core/services/tax-profile.service';
import { TenantService } from './core/services/tenant.service';
import { UnlayerService } from './core/services/unlayer.service';
import { UserEventService } from './core/services/user-event';
import { JobItemFactory } from './data/factories/job.item.factory';
import { RepoAccountService } from './data/repositories/repo-account.service';
import { RepoCommonService } from './data/repositories/repo-common.service';
import { RepoStartupService } from './data/repositories/repo-startup.service';
import { withBxGraphQLApolloConfig } from './graphql.module';
import { Nav2LayoutComponentsModule } from './nav2-layout-components.module';
import { AccountAddUserComponent, AccountSyncLogComponent, AccountUserDetailComponent } from './pages/account';
import { GenericProviderComponent } from './pages/account/integrations/generic-provider/generic-provider-settings.component';
/* Secondary Navigation */
import { SelectUserComponent } from './pages/account/users/select-user/select-user';
import { BuilderStatisticsModule } from './pages/builder-statistics';
import { AccessControlImportModule } from './pages/catalogues/catalogue-access-control/access-control-import/access-control-import.component';
import { UpdatePriceComponent } from './pages/catalogues/update-price/update-price.component';
// Ng generated angular-7 components. Need to use separate angular ng project
// as this project doesn't yet have angular project definition, but I started this
// in another branch and got it working.
import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';
import { appRoutes } from './app.routes';
import { BxAddressInputComponent } from './core/components/bx-address-input/bx-address-input.component';
import { CostItemsModalModule } from './core/components/cost-items-modal/cost-items-modal.component';
import { AppRouteReuseStrategy } from './core/routing/route-reuse-strategy';
import { RouterStateService } from './core/routing/router-state.service';
import { PreviewClientPortalModule } from './pages/client-portal/preview-client-portal/preview-client-portal.module';
import { AddClientModule } from './pages/clients/add-client/add-client.component';
import { ClientDetailsViewModule } from './pages/clients/client-details/client-details-view/client-details-view.component';
import { ImportClientsModule } from './pages/clients/import-clients/import-clients.component';
import { ContactModalComponent, ContactPersonViewComponent, ContactsImportComponent } from './pages/contacts';
import { QuickstartAreasSideformModule } from './pages/dashboard/dashboard-quickstart/quickstart-areas-sideform/quickstart-areas-sideform.component';
import { QuickstartSideformModule } from './pages/dashboard/dashboard-quickstart/quickstart-sideform/quickstart-sideform.component';
import { ReminderViewComponent } from './pages/dashboard/reminder-view.component';
import { ApplicationIntegrationsModule } from './pages/integrations-v2/application-integrations/application-integrations.module';
import { ErrorNotificationModule } from './pages/integrations-v2/components/bx-error-notification/bx-error-notification.component';
import { SupplierConnectionRequestModule } from './pages/integrations-v2/connection-request/request-connection.component';
import { InviteClientModule } from './pages/integrations-v2/invite-client/invite-client.component';
import { InviteSupplierModule } from './pages/integrations-v2/invite-supplier/invite-supplier.component';
import { ReassignSupplierConnectionComponent } from './pages/integrations-v2/reassign-supplier-connection/reassign-supplier-connection.component';
import { LayoutModule } from './pages/layout/layout.module';
import { MenubarEstimatesModule } from './pages/layout/navigation_secondary/menubar.estimates.component';
import { MenubarJobsModule } from './pages/layout/navigation_secondary/menubar.jobs.component';
import { TopbarEstimatesModule } from './pages/layout/navigation_secondary/topbar.estimates.component';
import { ModalComponent } from './pages/layout/notifications/modal/modal.component';
import { NotificationDirective } from './pages/layout/notifications/notification.directive';
import { NotificationErroredIntegrationComponent } from './pages/layout/notifications/notification.errored-integration.component';
import { NotificationLinkedConnectionsComponent } from './pages/layout/notifications/notification.linked-connections.component';
import { NotificationPaymentFailedComponent } from './pages/layout/notifications/notification.payment-failed.component';
import { NotificationReactivateComponent } from './pages/layout/notifications/notification.reactivate.component';
import { NotificationBannerService } from './pages/layout/notifications/notification.service';
import { NotificationTrialComponent } from './pages/layout/notifications/notification.trial.component';
import { NotificationVerfiyAccountComponent } from './pages/layout/notifications/notification.verfiy-account.component';
import { NotificationsComponent } from './pages/layout/notifications/notifications.component';
import { Sidebar2CheckboxDropdownComponent } from './pages/layout/sidebar.2/sidebar2-checkbox-dropdown.component';
import { Sidebar2RadioDropdownComponent } from './pages/layout/sidebar.2/sidebar2-radio-dropdown.component';
import { Sidebar2SubpanelLeadsComponent } from './pages/layout/sidebar.2/sidebar2-subpanel-leads/sidebar2-subpanel-leads.component';
import { Sidebar2SubpanelStateService } from './pages/layout/sidebar.2/sidebar2-subpanel-state.service';
import { Sidebar2SubpanelComponent } from './pages/layout/sidebar.2/sidebar2-subpanel.component';
import { Sidebar2ToggleService } from './pages/layout/sidebar.2/sidebar2-toggle.service';
import { Sidebar2Component } from './pages/layout/sidebar.2/sidebar2.component';
import { LeadAddEstimateModalModule } from './pages/leads/lead-add-estimate-modal/lead-add-estimate-modal.component';
import { CommsEmailSendModule } from './pages/messages';
import { ChatModule } from './pages/messages/conversations/chat/chat.module';
import { UiContactSelectorModule } from './pages/messages/ui-contact-selector/ui-contact-selector.module';
import { QuoteRequestViewModule } from './pages/quoterequests';
import { TakeOffModule } from './pages/takeoff/takeoff.module';
import { ReportsDateRangeComponent, ReportsSelectFormatComponent } from './reports';
import { SharedComponentsModule } from './shared-components.module';
import { BxHelpNoteWrapperModule } from './shared/bx-help-note-wrapper/bx-help-note-wrapper.component';
import { BxUserAvatarComponent } from './shared/bx-user-avatar';
import { HttpLoaderFactory } from './utils/helper-functions/translate.functions';
import { GlobalHeaderComponent } from './pages/layout/header/global-header.component';
import { provideApollo, provideNamedApollo } from 'apollo-angular';

registerLocaleData(localeAU);
registerLocaleData(localeNZ);
registerLocaleData(localeCA);
registerLocaleData(localeGB);

@NgModule({
    declarations: [
        AppComponent,
        ContactModalComponent,
        ContactPersonViewComponent,
        ContactsImportComponent,
        NotificationsComponent,
        NotificationDirective,
        NotificationTrialComponent,
        NotificationReactivateComponent,
        NotificationLinkedConnectionsComponent,
        NotificationErroredIntegrationComponent,
        NotificationPaymentFailedComponent,
        ModalComponent,
        SortByPipe,
        FormValidationDirective,
        Sidebar2SubpanelLeadsComponent,
        SanitizeInputTextDirective,
        /* Accounts */
        AccountUserDetailComponent,
        AccountAddUserComponent,
        AccountSyncLogComponent,
        GenericProviderComponent,
        SelectUserComponent,
        /* Integrations */
        ReportsSelectFormatComponent,
        ReportsDateRangeComponent,
        /* Pricelists and Recipes */
        UpdatePriceComponent,
        /* Sidebar new components */
        Sidebar2Component,
        Sidebar2SubpanelComponent,
        Sidebar2RadioDropdownComponent,
        Sidebar2CheckboxDropdownComponent,
        ReassignSupplierConnectionComponent,
        NotificationVerfiyAccountComponent,
        ReminderViewComponent,
        RfqResponseAttachmentsComponent,
        RfqResponseCostingitemsComponent,
        RfqResponseDetailsComponent,
        RfqResponseMessagesComponent,
        RfqResponseSideFormComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        MatTableModule,
        MatSortModule,
        MatInputModule,
        MatFormFieldModule,
        MatPaginatorModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatIconModule,
        NgSelectModule,
        FormsModule,
        BuilderStatisticsModule,
        ReactiveFormsModule,
        ModalModule.forRoot(),
        BxNoticesModule,
        BxDisclaimModule,
        BxStickyActionBarModule,
        BxPermissionNotificationModule,
        RouterModule.forRoot(appRoutes, {
            initialNavigation: 'enabledNonBlocking',
            paramsInheritanceStrategy: 'always',
            bindToComponentInputs: true,
        }),
        TooltipModule.forRoot(),
        NgxDatatableModule,
        ButtonsModule.forRoot(),
        BsDropdownModule.forRoot(),
        BsDatepickerModule.forRoot(),
        TypeaheadModule.forRoot(),
        ColorPickerModule,
        TabsModule.forRoot(),
        SharedComponentsModule,
        ProgressbarModule.forRoot(),
        Nav2LayoutComponentsModule,
        ScrollingModule,
        DragDropModule,
        BxGlobalNotificationsModule,
        BxSpinnerModule,
        BxTileModule,
        BxSelectableThumbnailModule,
        ApplicationIntegrationsModule,
        BxColorPickerModule,
        TopbarEstimatesModule,
        MenubarEstimatesModule,
        MenubarJobsModule,
        TaxRatesEditorModule,
        CategoryBreakdownModule,
        TaxRateSelectorModule,
        ItemBreakdownModule,
        QuoteRequestViewModule,
        ChatModule,
        MatChipsModule,
        MatExpansionModule,
        TimepickerModule.forRoot(),
        SelectPipeModule.forRoot(),
        BxHelpNoteWrapperModule,
        UiContactSelectorModule,
        TakeOffModule,
        AddClientModule,
        ImportClientsModule,
        ClientDetailsViewModule,
        LeadAddEstimateModalModule,
        ErrorNotificationModule,
        InviteClientModule,
        ToastrModule.forRoot(),
        BxDropdownMultiselectCheckboxModule,
        NgxSelectModule,
        PreviewClientPortalModule,
        CurrencyInputModule,
        QuickstartSideformModule,
        QuickstartAreasSideformModule,
        CommsEmailSendModule,
        InviteSupplierModule,
        SupplierConnectionRequestModule,
        AccessControlImportModule,
        LayoutModule,
        PhoneNumberLocalizePipeModule,
        CostItemsModalModule,
        BxUserAvatarComponent,
        NgScrollbar,
        BxAddressInputComponent,
        GlobalHeaderComponent,
    ],
    providers: [
        {
            provide: 'appConfig',
            useFactory: appConfig,
        },
        BsModalRef,
        BxPictureInPictureService,
        TitleCasePipe,
        DatePipe,
        ColourCodeConverterService,
        PasswordService,
        NotificationBannerService,
        TenantService,
        SortByPipe,
        LeadsService,
        InvoiceSharingService,
        MessagingService,
        CataloguesService,
        EstimatesV2Service,
        TaxProfileService,
        TenantService,
        EstimateCostingsService,
        CustomTemplateService,
        UnlayerService,
        Sidebar2SubpanelStateService,
        LocalStorageService,
        ColumnWidthsService,
        Sidebar2ToggleService,
        BxApiErrorService,
        JobItemFactory,
        RepoStartupService,
        RepoCommonService,
        RepoAccountService,
        DialogService,
        { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: BxApiErrorInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpCompressionInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: BusyOverlayInterceptor, multi: true, deps: [BusyOverlayService] },
        provideAppInitializer(() => {
            const initializerFn = initializeRoute();
            return initializerFn();
        }),
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        provideAppInitializer(() => {
            const initializerFn = initializeApp(
                inject(RepoStartupService),
                inject(StoresService),
                inject(UserEventService),
                inject(NotificationService),
                inject(ConnectivityService),
                inject(PendoFeedbackService),
                inject(FullStoryService),
                inject(AppVersionService),
                inject(AuthTokenService),
                inject(RouterStateService)
            );
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeThirdParty();
            return initializerFn();
        }),
        { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
        provideHttpClient(withInterceptorsFromDi()),
        provideNamedApollo(withBxGraphQLApolloConfig()),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
    ],
})
export class AppModule {}
